// Phase 25 iter-6 — Plans (Stripe SaaS para gandeiros).
//
// Mostra os dous niveis de plan + CTA que crea unha Checkout Session
// e redirixe a Stripe. Quen xa ten subscrición activa pode abrir o
// Customer Portal para xestionala (cancelar, cambiar tarxeta, etc.).

import { ArrowRight, Check, Sparkles } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { BillablePlan, SubscriptionResponse } from '@leyrochain/shared';

import { getSubscription, openCustomerPortal, startCheckoutSession } from '../api/billing';
import { describeAxiosError } from '../api/errors';
import ContentPage from '../components/ui/ContentPage';
import Spinner from '../components/ui/Spinner';
import { useAuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

const VALID_BILLABLE_PLANS: readonly BillablePlan[] = ['FARMER_BASIC', 'FARMER_PRO'];

interface PlanCard {
  id: BillablePlan | 'FREE';
  title: string;
  price: string;
  period: string;
  features: string[];
  recommended?: boolean;
}

const PLANS: PlanCard[] = [
  {
    id: 'FREE',
    title: 'Free',
    price: '0 €',
    period: 'sempre',
    features: [
      'Ata 10 animais',
      'Eventos sanitarios e traslados',
      'QR público para consumidores',
      'Sen comisión de venda os primeiros 30 días',
    ],
  },
  {
    id: 'FARMER_BASIC',
    title: 'Básico',
    price: '9,90 €',
    period: '/ mes',
    features: [
      'Ata 100 animais',
      'Anclaxe blockchain diaria incluida',
      'Soporte por email',
      'Exportación PDF do historial completo',
    ],
    recommended: true,
  },
  {
    id: 'FARMER_PRO',
    title: 'Pro',
    price: '24,90 €',
    period: '/ mes',
    features: [
      'Animais ilimitados',
      'API privada para integracións',
      'Sinatura veterinaria avanzada eIDAS',
      'Soporte prioritario (resposta < 4h)',
    ],
  },
];

export default function PlansPage(): JSX.Element {
  const { accessToken } = useAuthContext();
  const { addToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sub, setSub] = useState<SubscriptionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyPlan, setBusyPlan] = useState<BillablePlan | null>(null);
  const [openingPortal, setOpeningPortal] = useState(false);
  // Lote V-16 — auto-trigger checkout cando o usuario chega dende a
  // landing (chain.leyro.es/) cun `?plan=FARMER_BASIC|FARMER_PRO`.
  const autoTriggeredRef = useRef(false);

  useEffect(() => {
    if (!accessToken) {
      setLoading(false);
      return;
    }
    getSubscription()
      .then(setSub)
      .catch(() => {
        // Se falla, asumimos FREE para non bloquear a páxina pública.
        setSub({ plan: 'FREE', has_subscription: false, renews_at: null, trial_days: 0 });
      })
      .finally(() => setLoading(false));
  }, [accessToken]);

  // Auto-trigger Stripe checkout cando `?plan=` está presente, hai
  // accessToken e a subscrición non está xa activa. Limpa o query string
  // despois para idempotencia.
  useEffect(() => {
    if (autoTriggeredRef.current) return;
    if (loading || busyPlan) return;
    if (!accessToken) return;
    const planParam = searchParams.get('plan');
    if (!planParam || !VALID_BILLABLE_PLANS.includes(planParam as BillablePlan)) return;
    // Se o usuario xa ten ese plan, non disparamos checkout (sería duplicado).
    if (sub?.has_subscription && sub.plan === planParam) return;
    autoTriggeredRef.current = true;
    setSearchParams({}, { replace: true });
    (async () => {
      setBusyPlan(planParam as BillablePlan);
      try {
        const { url } = await startCheckoutSession(planParam as BillablePlan);
        window.location.assign(url);
      } catch (err) {
        addToast(describeAxiosError(err, 'Non se puido abrir o pagamento.'), 'error');
        setBusyPlan(null);
      }
    })();
  }, [loading, accessToken, busyPlan, searchParams, setSearchParams, sub, addToast]);

  async function handleCheckout(plan: BillablePlan) {
    setBusyPlan(plan);
    try {
      const { url } = await startCheckoutSession(plan);
      window.location.assign(url);
    } catch (err) {
      addToast(describeAxiosError(err, 'Non se puido abrir o pagamento.'), 'error');
      setBusyPlan(null);
    }
  }

  async function handlePortal() {
    setOpeningPortal(true);
    try {
      const { url } = await openCustomerPortal();
      window.location.assign(url);
    } catch (err) {
      addToast(describeAxiosError(err, 'Non se puido abrir o portal.'), 'error');
      setOpeningPortal(false);
    }
  }

  if (!accessToken) {
    // Mantén accesibilidade pública (a páxina describe o que ofrecemos);
    // o CTA "Subscribirme" leva ao login con next=plans cando o usuario
    // intenta contratar.
    return (
      <ContentPage
        title="Plans e prezo"
        intro="Subscríbete para escalar a túa explotación con LeyroChain. Cancela cando queiras dende o teu portal de cliente."
      >
        <PlansGrid
          plans={PLANS}
          subPlan={'FREE'}
          onCheckout={() => null}
          busyPlan={null}
          requireLogin
        />
      </ContentPage>
    );
  }

  return (
    <ContentPage
      title="Plans e prezo"
      intro="Subscríbete para escalar a túa explotación. Os cobros procésanse de forma segura; podes cancelar cando queiras."
    >
      {loading && (
        <div className="flex items-center gap-3 text-sm text-muted">
          <Spinner size={18} /> Cargando o teu plan…
        </div>
      )}

      {sub && (
        <p className="not-prose text-sm text-muted mb-6">
          O teu plan actual:{' '}
          <strong className="text-dark">
            {sub.plan === 'FREE' ? 'Free' : sub.plan === 'FARMER_BASIC' ? 'Básico' : 'Pro'}
          </strong>
          {sub.renews_at
            ? ` · Renóvase o ${new Date(sub.renews_at).toLocaleDateString('gl-ES')}`
            : ''}
          {sub.has_subscription && (
            <>
              {' '}
              ·{' '}
              <button
                type="button"
                onClick={() => void handlePortal()}
                disabled={openingPortal}
                className="text-rauxa font-semibold hover:underline disabled:opacity-60"
              >
                {openingPortal ? <Spinner size={12} /> : <>Xestionar subscrición →</>}
              </button>
            </>
          )}
        </p>
      )}

      <PlansGrid
        plans={PLANS}
        subPlan={sub?.plan ?? 'FREE'}
        onCheckout={(p) => void handleCheckout(p)}
        busyPlan={busyPlan}
      />

      <h2>Como funciona o pagamento?</h2>
      <p>
        Tras pulsar a túa elección, levámoste a unha páxina de pagamento seguro para introducir os
        datos da túa tarxeta. A primeira renovación faise un mes despois — antes diso podes cancelar
        sen custo dende o teu portal.
      </p>
      <p>
        Os datos fiscais (NIF, dirección) podes incluílos no pagamento para que as facturas saian
        coa información correcta. Consultas a calquera factura no portal cando queiras.
      </p>
    </ContentPage>
  );
}

function PlansGrid({
  plans,
  subPlan,
  onCheckout,
  busyPlan,
  requireLogin = false,
}: {
  plans: PlanCard[];
  subPlan: string;
  onCheckout: (p: BillablePlan) => void;
  busyPlan: BillablePlan | null;
  requireLogin?: boolean;
}) {
  return (
    <div className="not-prose grid sm:grid-cols-3 gap-4 mb-10">
      {plans.map((p) => {
        const isCurrent = subPlan === p.id;
        const isBillable = p.id !== 'FREE';
        return (
          <div
            key={p.id}
            className={`flex flex-col rounded-3xl p-6 border ${
              p.recommended ? 'border-rauxa shadow-card' : 'border-border'
            } bg-white`}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-dark">{p.title}</h3>
              {p.recommended && (
                <span className="text-[10px] uppercase tracking-wide font-bold text-rauxa bg-rauxa/10 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                  <Sparkles size={10} /> Popular
                </span>
              )}
            </div>
            <div className="mt-2 flex items-baseline gap-1">
              <span className="text-3xl font-extrabold text-dark">{p.price}</span>
              <span className="text-sm text-muted">{p.period}</span>
            </div>
            <ul className="mt-4 space-y-2 flex-1">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-dark">
                  <Check size={16} className="text-rauxa shrink-0 mt-0.5" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <div className="mt-5">
              {isCurrent ? (
                <span className="block text-center text-sm font-semibold text-muted">
                  Plan actual
                </span>
              ) : !isBillable ? (
                <span className="block text-center text-sm text-muted">Incluído por defecto</span>
              ) : requireLogin ? (
                <a
                  href={`/marketplace/login?next=${encodeURIComponent(`/marketplace/plans?plan=${p.id}`)}`}
                  className="btn-primary w-full"
                >
                  Iniciar sesión
                </a>
              ) : (
                <button
                  type="button"
                  onClick={() => onCheckout(p.id as BillablePlan)}
                  disabled={busyPlan !== null}
                  className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {busyPlan === p.id ? (
                    <Spinner size={16} />
                  ) : (
                    <>
                      Subscribirme
                      <ArrowRight size={14} />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
